import { Address, Regions } from 'graphql/types'
import { CLOUDINARY_IMAGE_URL } from 'config'
import { TLocation } from 'pages/regioes-atendidas/regiao/types'
import * as events from 'pages/regioes-atendidas/regiao/events'
import { scrollToElement } from './dom'
import { pluralize } from './text'
import { toCurrency, toShortPrice } from './number'

export type TCard = {
  title: string
  value: string | number
  button?: {
    label: string
    href?: string
    onClick?: () => void
  }
}

type TGetDataCardsParams = {
  locationType: TLocation
  region?: Regions
  searchUrl: string
}

const EMPTY_PLACEHOLDER = '-'

const REGION_PLACEHOLDER_IMAGE_URL = 'site/regions/Placeholder.webp'

const AVERAGE_PERCENTAGE = 0.15
const SECTION_Y_OFFSET = -60

const getAveragePrice = (value: number, percentage = AVERAGE_PERCENTAGE) => {
  const percentageValue = percentage * value
  return {
    minimum: (value - percentageValue).toFixed(0),
    maximum: (value + percentageValue).toFixed(0),
  }
}

export const getDataCards = ({
  locationType,
  region,
  searchUrl,
}: TGetDataCardsParams) => {
  const averageListingPrice = getAveragePrice(
    region?.information?.averagePrice || 0,
  )

  const averageAreaPrice = getAveragePrice(
    Number((region?.information?.averagePricePerMeter || 0).toFixed(3)),
  )

  const BASE_SEARCH_URL = searchUrl
  const averagePricePerMeterUrl = `${BASE_SEARCH_URL}?precoM2De-${averageAreaPrice.minimum}-ate-${averageAreaPrice.maximum}`

  const averagePricePerMeter = {
    title: 'Média de valor do m²',
    value: toCurrency(region?.information?.averagePricePerMeter),
    button: {
      label: 'Buscar nessa média',
      href: averagePricePerMeterUrl,
    },
  }

  const neighborhoodTypeUrl = `${BASE_SEARCH_URL}/${
    region?.information?.listing?.rooms || 0
  }-quartos/${
    region?.information?.listing?.garageSpots
      ? `${region?.information?.listing?.garageSpots}-vagas`
      : ''
  }`
  const neighborhoodPriceUrl = `${BASE_SEARCH_URL}?de-${
    region?.information?.minimumPrice || 0
  }-ate-${region?.information?.maximumPrice || 0}`
  const neighborhoodAveragePriceUrl = `${BASE_SEARCH_URL}?de-${averageListingPrice.minimum}-ate-${averageListingPrice.maximum}`

  const cityCards: Array<TCard> =
    locationType === 'city'
      ? [
          {
            title: 'População total',
            value:
              region?.information?.demographicData?.population?.toLocaleString(
                'pt-BR',
              ) || EMPTY_PLACEHOLDER,
          },
          {
            ...averagePricePerMeter,
            button: {
              ...averagePricePerMeter.button,
              onClick: () =>
                events.cityDataCardButtonEvent(
                  averagePricePerMeterUrl,
                  averagePricePerMeter.button.label,
                ),
            },
          },
          {
            title: 'Quantidade de bairros atendidos',
            value: region?.regions?.length || EMPTY_PLACEHOLDER,
            button: {
              label: 'Conhecer os bairros',
              onClick: () => {
                events.cityDataCardButtonEvent('', 'Conhecer os bairros')
                scrollToElement('locationsServedSection', SECTION_Y_OFFSET)
              },
            },
          },
          {
            title: 'Quantidade de imóveis anunciados',
            value: region?.listingCount || EMPTY_PLACEHOLDER,
            button: {
              label: 'Buscar imóveis',
              href: BASE_SEARCH_URL,
              onClick: () =>
                events.cityDataCardButtonEvent(
                  BASE_SEARCH_URL,
                  'Buscar imóveis',
                ),
            },
          },
        ]
      : []

  const neighborhoodCards: Array<TCard> =
    locationType === 'neighborhood'
      ? [
          {
            title: 'Principal tipo de imóvel',
            value: `${region?.information?.listing?.type || ''} com ${
              region?.information?.listing?.rooms || 0
            } ${pluralize({
              count: region?.information?.listing?.rooms || 0,
              noun: 'quarto',
            })} ${
              region?.information?.listing?.garageSpots
                ? `e ${region?.information?.listing?.garageSpots} ${pluralize({
                    count: region?.information?.listing?.garageSpots,
                    noun: 'vaga',
                  })}`
                : ''
            }`,
            button: {
              label: 'Ver imóveis similares',
              href: neighborhoodTypeUrl,
              onClick: () =>
                events.neighborhoodDataCardButtonEvent(
                  neighborhoodTypeUrl,
                  'Ver imóveis similares',
                ),
            },
          },
          {
            ...averagePricePerMeter,
            button: {
              ...averagePricePerMeter.button,
              onClick: () =>
                events.neighborhoodDataCardButtonEvent(
                  averagePricePerMeterUrl,
                  averagePricePerMeter.button.label,
                ),
            },
          },
          {
            title: 'Faixa de preço dos anúncios',
            value: `${toShortPrice(region?.information?.minimumPrice || 0, {
              completeSuffix: true,
            })} até ${toShortPrice(region?.information?.maximumPrice || 0, {
              completeSuffix: true,
            })}`,
            button: {
              label: 'Buscar nessa faixa',
              href: neighborhoodPriceUrl,
              onClick: () =>
                events.neighborhoodDataCardButtonEvent(
                  neighborhoodPriceUrl,
                  'Buscar nessa faixa',
                ),
            },
          },
          {
            title: 'Média de anúncio',
            value: `${toCurrency(region?.information?.averagePrice)}`,
            button: {
              label: 'Buscar nessa média',
              href: neighborhoodAveragePriceUrl,
              onClick: () =>
                events.neighborhoodDataCardButtonEvent(
                  neighborhoodAveragePriceUrl,
                  'Buscar nessa média',
                ),
            },
          },
        ]
      : []

  return [...cityCards, ...neighborhoodCards]
}

export const locationServedTitleMap = {
  city: 'Bairros atendidos',
  neighborhood: 'Ruas atendidas',
  condominium: 'Condominios em',
}

export const getRegionImageUrl = (imageFilename?: string | null) =>
  `${CLOUDINARY_IMAGE_URL}/${imageFilename || REGION_PLACEHOLDER_IMAGE_URL}`

export const getRegionPageTitle = ({
  currentLocation,
  address,
  locationType,
}: {
  currentLocation: string
  address: Address
  locationType: TLocation
}) => {
  const location = {
    city: `${currentLocation} - ${address.state as string}`,
    neighborhood: currentLocation,
    condominium: currentLocation,
  }[locationType]

  return `Saiba como é morar em ${location} | EmCasa`
}

export const getRegionPageDescription = ({
  currentLocation,
  address,
  locationType,
}: {
  currentLocation: string
  address: Address
  locationType: TLocation
}) => {
  const description = {
    city: `Descubra informações como população total, valor do m2, bairros atendidos e imóveis à venda em ${currentLocation} - ${
      address.state as string
    }`,
    neighborhood: `Quer saber como é morar em ${currentLocation}? Acesse e confira informações como opções de transporte, educação, saúde, lazer e imóveis à venda.`,
    condominium: `Quer saber como é morar em ${currentLocation}? Acesse e confira informações como opções de transporte, educação, saúde, lazer e imóveis à venda.`,
  }[locationType]

  return description
}
